import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Zap,
  CheckCircle2,
  XCircle,
  Flame,
  Layers,
  ShieldCheck,
  ArrowRight,
  SlidersHorizontal,
} from "lucide-react";

export const FreshNeoBrutalistLight: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy brutalist springs
  const introSpring = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 180, mass: 0.8 },
  });

  const cardSpring1 = spring({
    frame: frame - 6,
    fps,
    config: { damping: 15, stiffness: 160 },
  });

  const cardSpring2 = spring({
    frame: frame - 12,
    fps,
    config: { damping: 15, stiffness: 160 },
  });

  const cardSpring3 = spring({
    frame: frame - 18,
    fps,
    config: { damping: 15, stiffness: 160 },
  });

  // Marquee tape motion
  const tapeSlide = interpolate(frame, [0, 240], [0, -360]);

  // Color tokens for Authentic Neo-Brutalism Light (Anti-AI-Slop)
  const canvasBg = "#F4F0EA"; // Warm tactile eggshell / raw paper
  const inkBlack = "#000000";
  const pureWhite = "#FFFFFF";
  const canaryYellow = "#FFE500"; // Warm industrial warning yellow (zero green tint)
  const hazardOrange = "#FF5500"; // Vivid industrial orange
  const coralRed = "#FF4365"; // Punchy warning coral
  const cobaltBlue = "#1B44FF"; // Deep electric royal blue

  return (
    <AbsoluteFill
      style={{
        backgroundColor: canvasBg,
        color: inkBlack,
        fontFamily: "'Space Grotesk', 'Inter', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. TACTILE ARCHITECTURAL CORNER MARKS (ZERO AI GRID) */}
      <div
        style={{
          position: "absolute",
          top: 36,
          left: 40,
          fontFamily: "'Anton', sans-serif",
          fontSize: 32,
          fontWeight: 900,
          color: inkBlack,
          lineHeight: 1,
        }}
      >
        +
      </div>
      <div
        style={{
          position: "absolute",
          top: 36,
          right: 40,
          fontFamily: "'Anton', sans-serif",
          fontSize: 32,
          fontWeight: 900,
          color: inkBlack,
          lineHeight: 1,
        }}
      >
        +
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 40,
          left: 40,
          fontFamily: "'Anton', sans-serif",
          fontSize: 32,
          fontWeight: 900,
          color: inkBlack,
          lineHeight: 1,
        }}
      >
        +
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 40,
          right: 40,
          fontFamily: "'Anton', sans-serif",
          fontSize: 32,
          fontWeight: 900,
          color: inkBlack,
          lineHeight: 1,
        }}
      >
        +
      </div>

      {/* 2. INDUSTRIAL HAZARD MARQUEE TAPE (TOP BANNER) */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: -80,
          right: -80,
          height: 52,
          backgroundColor: canaryYellow,
          borderTop: `4px solid ${inkBlack}`,
          borderBottom: `4px solid ${inkBlack}`,
          display: "flex",
          alignItems: "center",
          transform: "rotate(-1.8deg)",
          overflow: "hidden",
          boxShadow: `0 8px 0px ${inkBlack}`,
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 40,
            whiteSpace: "nowrap",
            fontSize: 22,
            fontFamily: "'Anton', 'Bebas Neue', sans-serif",
            fontWeight: 900,
            letterSpacing: "0.14em",
            transform: `translateX(${tapeSlide}px)`,
          }}
        >
          <span>/// NEO-BRUTALISM LIGHT SPEC /// ZERO GLOW /// ZERO AI-SLOP /// 100% CRISP CONTRAST ///</span>
          <span>/// NEO-BRUTALISM LIGHT SPEC /// ZERO GLOW /// ZERO AI-SLOP /// 100% CRISP CONTRAST ///</span>
          <span>/// NEO-BRUTALISM LIGHT SPEC /// ZERO GLOW /// ZERO AI-SLOP /// 100% CRISP CONTRAST ///</span>
          <span>/// NEO-BRUTALISM LIGHT SPEC /// ZERO GLOW /// ZERO AI-SLOP /// 100% CRISP CONTRAST ///</span>
        </div>
      </div>

      {/* 3. MAIN CONTENT STAGE (CENTERED TO TIKTOK SAFE ZONE: Y 220 - 1520px, Width 940px) */}
      <div
        style={{
          position: "absolute",
          top: 220,
          left: 0,
          right: 0,
          height: 1300,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0 70px",
          zIndex: 20,
          boxSizing: "border-box",
        }}
      >
        {/* TOP STATUS PILL & CATEGORY */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            opacity: interpolate(introSpring, [0, 1], [0, 1]),
          }}
        >
          <div
            style={{
              backgroundColor: inkBlack,
              color: pureWhite,
              padding: "8px 20px",
              fontSize: 18,
              fontWeight: 800,
              fontFamily: "'Space Grotesk', sans-serif",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: `4px 4px 0px ${hazardOrange}`,
              border: `3px solid ${inkBlack}`,
            }}
          >
            <SlidersHorizontal size={18} strokeWidth={3} color={canaryYellow} />
            DESIGN SPEC v2.4
          </div>

          <div
            style={{
              backgroundColor: pureWhite,
              color: inkBlack,
              padding: "8px 18px",
              fontSize: 18,
              fontWeight: 800,
              border: `3px solid ${inkBlack}`,
              boxShadow: `4px 4px 0px ${inkBlack}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <ShieldCheck size={20} strokeWidth={2.5} color={hazardOrange} />
            ANTI-AI-SLOP CERTIFIED
          </div>
        </div>

        {/* HERO TITLE CARD (High Contrast Pure White on Warm Canvas) */}
        <div
          style={{
            width: "100%",
            backgroundColor: pureWhite,
            border: `4px solid ${inkBlack}`,
            boxShadow: `10px 10px 0px ${inkBlack}`,
            padding: "36px 36px 30px 36px",
            marginBottom: 24,
            boxSizing: "border-box",
            transform: `translateY(${interpolate(cardSpring1, [0, 1], [40, 0])}px)`,
            opacity: cardSpring1,
            position: "relative",
          }}
        >
          {/* Tilted Stamp Tag */}
          <div
            style={{
              position: "absolute",
              top: -20,
              right: 32,
              backgroundColor: canaryYellow,
              color: inkBlack,
              padding: "6px 16px",
              fontFamily: "'Anton', sans-serif",
              fontSize: 20,
              fontWeight: 900,
              letterSpacing: "0.08em",
              border: `3px solid ${inkBlack}`,
              boxShadow: `4px 4px 0px ${inkBlack}`,
              transform: "rotate(2.5deg)",
            }}
          >
            LIGHT THEME DONE RIGHT
          </div>

          <div
            style={{
              fontSize: 18,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: hazardOrange,
              textTransform: "uppercase",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Zap size={22} strokeWidth={3} />
            RAW TACTILE EDITORIAL
          </div>

          <div
            style={{
              fontSize: 66,
              fontFamily: "'Anton', 'Bebas Neue', sans-serif",
              lineHeight: 0.96,
              letterSpacing: "-0.01em",
              color: inkBlack,
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            NEO-BRUTALISM <span style={{ backgroundColor: canaryYellow, padding: "0 8px" }}>TERANG</span> BUKAN AI SLOP
          </div>

          <div
            style={{
              fontSize: 22,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              lineHeight: 1.35,
              color: "#333333",
            }}
          >
            Bukan clay 3D lembek, bukan gradien neon ungu murahan. Kuncinya di <strong>kanvas kertas matte</strong>, <strong>garis hitam pekat 4px</strong>, dan <strong>hard drop shadow 0-blur</strong>.
          </div>
        </div>

        {/* COMPARISON BENTO GRID: AI SLOP vs AUTHENTIC BRUTALIST */}
        <div
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 24,
            boxSizing: "border-box",
            transform: `translateY(${interpolate(cardSpring2, [0, 1], [40, 0])}px)`,
            opacity: cardSpring2,
          }}
        >
          {/* Card Left: Ciri AI Slop (Banned) */}
          <div
            style={{
              backgroundColor: "#FFF2F4",
              border: `4px solid ${inkBlack}`,
              boxShadow: `7px 7px 0px ${inkBlack}`,
              padding: "24px 22px",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backgroundColor: coralRed,
                color: pureWhite,
                padding: "4px 12px",
                fontSize: 16,
                fontWeight: 900,
                fontFamily: "'Space Grotesk', sans-serif",
                border: `2px solid ${inkBlack}`,
                marginBottom: 16,
              }}
            >
              <XCircle size={16} strokeWidth={3} />
              AI SLOP (SAMPAH)
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#444444", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: coralRed, fontWeight: 900 }}>✕</span>
                Gradien ungu-biru mengapung
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#444444", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: coralRed, fontWeight: 900 }}>✕</span>
                Glassmorphism blur berantakan
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#444444", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: coralRed, fontWeight: 900 }}>✕</span>
                Grid garis matrix murahan
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#444444", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: coralRed, fontWeight: 900 }}>✕</span>
                Teks hiasan tidak terbaca
              </div>
            </div>
          </div>

          {/* Card Right: Neo-Brutalist Asli (Clean Spec) */}
          <div
            style={{
              backgroundColor: canaryYellow,
              border: `4px solid ${inkBlack}`,
              boxShadow: `7px 7px 0px ${inkBlack}`,
              padding: "24px 22px",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backgroundColor: inkBlack,
                color: canaryYellow,
                padding: "4px 12px",
                fontSize: 16,
                fontWeight: 900,
                fontFamily: "'Space Grotesk', sans-serif",
                border: `2px solid ${inkBlack}`,
                marginBottom: 16,
              }}
            >
              <CheckCircle2 size={16} strokeWidth={3} />
              NEO-BRUTALIST TEPAT
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: inkBlack, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: inkBlack, fontWeight: 900 }}>✓</span>
                Kanvas Eggshell Matte (#F4F0EA)
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: inkBlack, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: inkBlack, fontWeight: 900 }}>✓</span>
                Drop shadow solid 0-blur (8px)
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: inkBlack, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: inkBlack, fontWeight: 900 }}>✓</span>
                Border hitam 3.5px - 4px kaku
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: inkBlack, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: inkBlack, fontWeight: 900 }}>✓</span>
                Display font bold & tegas
              </div>
            </div>
          </div>
        </div>

        {/* 3 RULES FOR VIDEO CARDS (Bento Feature Row) */}
        <div
          style={{
            width: "100%",
            backgroundColor: pureWhite,
            border: `4px solid ${inkBlack}`,
            boxShadow: `8px 8px 0px ${inkBlack}`,
            padding: "26px 28px",
            marginBottom: 26,
            boxSizing: "border-box",
            transform: `translateY(${interpolate(cardSpring3, [0, 1], [40, 0])}px)`,
            opacity: cardSpring3,
          }}
        >
          <div
            style={{
              fontSize: 17,
              fontWeight: 900,
              fontFamily: "'Space Grotesk', sans-serif",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: inkBlack,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Layers size={20} strokeWidth={2.5} color={hazardOrange} />
              PALET WARNA TEMA TERANG (HIGH VOLTAGE)
            </span>
            <span
              style={{
                fontSize: 14,
                backgroundColor: "#EEEEEE",
                padding: "3px 8px",
                border: `2px solid ${inkBlack}`,
              }}
            >
              HEX CODES
            </span>
          </div>

          {/* Palette Swatches Bar */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
            }}
          >
            <div
              style={{
                backgroundColor: "#F4F0EA",
                border: `3px solid ${inkBlack}`,
                padding: "12px 10px",
                textAlign: "center",
                boxShadow: `3px 3px 0px ${inkBlack}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: "#666" }}>CANVAS</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: inkBlack }}>#F4F0EA</div>
            </div>

            <div
              style={{
                backgroundColor: canaryYellow,
                border: `3px solid ${inkBlack}`,
                padding: "12px 10px",
                textAlign: "center",
                boxShadow: `3px 3px 0px ${inkBlack}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: inkBlack }}>CANARY</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: inkBlack }}>#FFE500</div>
            </div>

            <div
              style={{
                backgroundColor: hazardOrange,
                color: pureWhite,
                border: `3px solid ${inkBlack}`,
                padding: "12px 10px",
                textAlign: "center",
                boxShadow: `3px 3px 0px ${inkBlack}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FFF" }}>ORANGE</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: pureWhite }}>#FF5500</div>
            </div>

            <div
              style={{
                backgroundColor: cobaltBlue,
                color: pureWhite,
                border: `3px solid ${inkBlack}`,
                padding: "12px 10px",
                textAlign: "center",
                boxShadow: `3px 3px 0px ${inkBlack}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FFF" }}>COBALT</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: pureWhite }}>#1B44FF</div>
            </div>
          </div>
        </div>

        {/* BOTTOM CALLOUT & ACTION BUTTON */}
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: hazardOrange,
            border: `4px solid ${inkBlack}`,
            boxShadow: `8px 8px 0px ${inkBlack}`,
            padding: "18px 28px",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                backgroundColor: pureWhite,
                border: `3px solid ${inkBlack}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Flame size={28} strokeWidth={2.5} color={hazardOrange} />
            </div>
            <div>
              <div style={{ fontSize: 24, fontFamily: "'Anton', sans-serif", color: pureWhite, letterSpacing: "0.04em", lineHeight: 1.1 }}>
                COCOK UNTUK VIDEO TIKTOK / REELS
              </div>
              <div style={{ fontSize: 16, fontFamily: "'Inter', sans-serif", fontWeight: 700, color: canaryYellow }}>
                Pecah di layar HP kecil, teks kebaca seketika tanpa squinting!
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: pureWhite,
              color: inkBlack,
              padding: "10px 20px",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 16,
              fontWeight: 900,
              border: `3px solid ${inkBlack}`,
              boxShadow: `4px 4px 0px ${inkBlack}`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            LIHAT SPEC
            <ArrowRight size={18} strokeWidth={3} />
          </div>
        </div>

        {/* TIKTOK SUBTITLE PILL SAFE ZONE (Y ~ 1480px, bottom 380px) */}
        <div
          style={{
            position: "absolute",
            bottom: 30,
            backgroundColor: inkBlack,
            color: pureWhite,
            border: `3.5px solid ${inkBlack}`,
            boxShadow: `6px 6px 0px ${canaryYellow}`,
            padding: "14px 32px",
            fontSize: 22,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 800,
            letterSpacing: "0.02em",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ color: canaryYellow }}>●</span>
          "Kanvas kertas matte + border tebal 4px = visual nendang & bebas AI slop."
        </div>
      </div>
    </AbsoluteFill>
  );
};
